import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

type Service = {
  icon: string;
  title: string;
  benefit: string;
  tag: string;
  img: string;
  featured?: boolean;
};

const SERVICES: Service[] = [
  {
    icon: 'ri-landscape-line',
    title: 'Landscape Design',
    benefit: 'Custom blueprints that transform your vision into a living, breathing outdoor masterpiece tailored to your property.',
    tag: 'Signature Service',
    featured: true,
    img: 'https://brainardsgreenscapes.com/assets/images/designandbuildhomepage.jpg',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Hardscaping & Patios',
    benefit: 'Precision-crafted stone patios, retaining walls, and walkways built to endure decades with lasting beauty.',
    tag: 'Most Popular',
    img: 'https://brainardsgreenscapes.com/assets/images/hardscapinghome.jpg',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Artificial Grass Installation',
    benefit: 'Perfect green yard year-round with minimal upkeep, no watering, and no patchy lawn — ever again.',
    tag: 'Low Maintenance',
    img: 'https://brainardsgreenscapes.com/assets/images/artifical%20grass.jpg',
  },
  {
    icon: 'ri-plant-line',
    title: 'Xeriscaping & Rock Work',
    benefit: 'Beautiful drought-resistant landscapes with native plants and rock features that thrive in New Mexico\'s climate.',
    tag: 'Desert Smart',
    img: 'https://brainardsgreenscapes.com/assets/images/Xeriscapehome.jpg',
  },
  {
    icon: 'ri-drop-line',
    title: 'Drip Irrigation Systems',
    benefit: 'Smart, efficient water management that keeps your landscape thriving year-round while reducing water costs.',
    tag: 'Smart Systems',
    img: 'https://brainardsgreenscapes.com/assets/images/about.jpg',
  },
  {
    icon: 'ri-tools-line',
    title: 'Residential Landscaping',
    benefit: 'Full-service residential landscape design, installation, and maintenance — your property, perfected.',
    tag: 'Full Service',
    featured: true,
    img: 'https://brainardsgreenscapes.com/assets/images/whatwehave.jpg',
  },
];

export default function ServicesOverview() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });

  const featuredTop = SERVICES[0];
  const midServices = SERVICES.slice(1, 5);
  const featuredBottom = SERVICES[5];

  return (
    <section id="services" className="bg-ivory-100 py-28">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <p className="font-sans text-forest-500 text-xs tracking-[0.25em] uppercase mb-3">What We Do</p>
            <h2 className="font-serif text-charcoal-900 font-semibold leading-tight" style={{ fontSize: 'clamp(36px, 4vw, 54px)' }}>
              Full-Service Landscape Design<br />&amp; Outdoor Construction
            </h2>
          </div>
          <p className="font-sans text-charcoal-700 text-base leading-relaxed max-w-xs md:text-right">
            From the first sketch to the final stone placement — we handle every detail of your transformation.
          </p>
        </div>

        {/* Top Featured */}
        <FeaturedCard service={featuredTop} delay={0} />

        {/* Mid grid 4-up */}
        <div className="grid grid-cols-2 gap-5 mt-5">
          {midServices.map((svc, i) => (
            <SmallCard key={svc.title} service={svc} delay={i * 80} />
          ))}
        </div>

        {/* Bottom Featured */}
        <div className="mt-5">
          <FeaturedCard service={featuredBottom} delay={100} />
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ service, delay }: { service: Service; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`relative w-full h-[420px] rounded-lg overflow-hidden group cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <img
        src={service.img}
        alt={service.title}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/85 via-charcoal-900/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/40 to-transparent" />

      <div className="absolute bottom-0 left-0 right-0 p-10">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-sans text-stone-400 text-[10px] tracking-[0.2em] uppercase border border-stone-500/40 px-3 py-1 rounded-full">
            {service.tag}
          </span>
        </div>
        <div className="flex items-start justify-between gap-8">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${service.icon} text-stone-300 text-lg`} />
              </div>
              <h3 className="font-serif text-white text-3xl font-semibold">{service.title}</h3>
            </div>
            <p className="font-sans text-stone-300 text-base leading-relaxed max-w-xl">{service.benefit}</p>
          </div>
          <a
            href="#consultation"
            onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="flex-shrink-0 flex items-center gap-2 border border-white/30 hover:border-white/60 text-white hover:bg-white/10 font-sans text-sm font-medium px-6 py-3 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            Get a Quote
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}

function SmallCard({ service, delay }: { service: Service; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`relative h-72 rounded-lg overflow-hidden group cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <img
        src={service.img}
        alt={service.title}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/80 via-charcoal-900/20 to-transparent" />

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <span className="font-sans text-stone-400 text-[10px] tracking-[0.18em] uppercase mb-2 block">{service.tag}</span>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${service.icon} text-stone-300 text-base`} />
          </div>
          <h3 className="font-serif text-white text-xl font-semibold">{service.title}</h3>
        </div>
        <p className="font-sans text-stone-300 text-sm leading-relaxed mb-4">{service.benefit}</p>
        <a
          href="#consultation"
          onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="inline-flex items-center gap-1.5 font-sans text-stone-300 hover:text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          Explore Service
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-line" />
          </div>
        </a>
      </div>
    </div>
  );
}
